(() => {
  'use strict';

  if (customElements.get('platform-switcher')) return;

  let sequence = 0;

  customElements.define(
    'platform-switcher',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        const trigger = this.querySelector('.platform-switcher__trigger');
        const panel = this.querySelector('.platform-switcher__panel');

        if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
        this.controller = new AbortController();

        const options = {
          signal: this.controller.signal,
        };

        const nativePopover = typeof panel.showPopover === 'function';

        panel.id = `PlatformSwitcherPanel-${++sequence}`;
        trigger.setAttribute('aria-controls', panel.id);

        const isOpen = () => this.dataset.open !== undefined;

        /** @param {boolean} open */
        const sync = (open) => {
          this.toggleAttribute('data-open', open);
          trigger.setAttribute('aria-expanded', String(open));
        };

        const position = () => {
          if (!isOpen()) return;

          const rect = trigger.getBoundingClientRect();
          const width = Math.min(420, window.innerWidth - 24);

          const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));

          const below = window.innerHeight - rect.bottom - 20;
          const above = rect.top - 20;
          const useAbove = below < 280 && above > below;
          const available = Math.max(80, useAbove ? above : below);

          panel.style.width = `${width}px`;
          panel.style.maxHeight = `${available}px`;
          panel.style.left = `${left}px`;

          panel.style.top = `${
            useAbove ? Math.max(12, rect.top - panel.offsetHeight - 8) : rect.bottom + 8
          }px`;
        };

        const close = (restoreFocus = false) => {
          if (nativePopover && panel.matches(':popover-open')) {
            panel.hidePopover();
          }

          sync(false);

          if (restoreFocus) trigger.focus();
        };

        this.closePanel = close;

        document.addEventListener(
          'littleg:overlay:close-others',
          (event) => {
            const detail = 'detail' in event ? event.detail : null;
            if (
              typeof detail === 'object' &&
              detail !== null &&
              'except' in detail &&
              detail.except === 'platform-switcher'
            )
              return;
            close();
          },
          options
        );

        trigger.addEventListener(
          'click',
          () => {
            if (isOpen()) {
              close();
              return;
            }

            document.dispatchEvent(
              new CustomEvent('littleg:overlay:close-others', {
                detail: {
                  except: 'platform-switcher',
                  source: 'platform-switcher',
                },
              })
            );

            sync(true);

            if (nativePopover) panel.showPopover();

            position();
          },
          options
        );

        panel.addEventListener(
          'beforetoggle',
          (event) => {
            sync('newState' in event && event.newState === 'open');
          },
          options
        );

        panel.addEventListener(
          'toggle',
          () => {
            if (nativePopover) {
              sync(panel.matches(':popover-open'));
            }
          },
          options
        );

        document.addEventListener(
          'pointerdown',
          (event) => {
            if (!(event.target instanceof Node) || !this.contains(event.target)) close();
          },
          options
        );

        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape' && isOpen()) {
              close(this.contains(document.activeElement));
            }
          },
          options
        );

        this.addEventListener(
          'focusout',
          (event) => {
            if (!this.contains(event.relatedTarget)) close();
          },
          options
        );

        panel.addEventListener(
          'click',
          (event) => {
            if (event.target instanceof Element && event.target.closest('a')) close();
          },
          options
        );

        window.addEventListener('resize', position, options);

        document.addEventListener(
          'scroll',
          (event) => {
            if (!(event.target instanceof Node) || !panel.contains(event.target)) close();
          },
          {
            ...options,
            capture: true,
            passive: true,
          }
        );

        sync(false);
      }

      disconnectedCallback() {
        this.closePanel?.();
        this.controller?.abort();

        this.controller = null;
        this.closePanel = null;
      }
    }
  );
})();
